import type { GlManualEntrySource, GlManualEntryStatus } from '../generated/enums.js';

export const MANUAL_ENTRY_ALLOWED_DOCUMENT_TYPES = ['MANUAL', 'ADJUSTMENT'] as const;

export type ManualEntryDocumentType = (typeof MANUAL_ENTRY_ALLOWED_DOCUMENT_TYPES)[number];

export const MANUAL_ENTRY_LIST_ORDER_BY = [
  'entryNumber',
  'entity',
  'journalDate',
  'documentType',
  'status',
  'createdBy',
  'description',
  'debit',
  'credit',
] as const;

export type ManualEntryListOrderBy = (typeof MANUAL_ENTRY_LIST_ORDER_BY)[number];

export const MANUAL_ENTRY_LIST_ORDER_DIR = ['asc', 'desc'] as const;

export type ManualEntryListOrderDir = (typeof MANUAL_ENTRY_LIST_ORDER_DIR)[number];

export interface ManualEntryLineRequest {
  glAccountId: string;
  debitAmount: number;
  creditAmount: number;
  description?: string;
  costCenterId?: string;
  departmentId?: string;
  managerialAccountId?: string;
}

export interface CreateManualEntryRequest {
  entityId: string;
  journalDate: string;
  description?: string;
  documentType?: string;
  autoReverse?: boolean;
  lines: ManualEntryLineRequest[];
}

export interface UpdateManualEntryRequest {
  journalDate?: string;
  description?: string;
  documentType?: string;
  autoReverse?: boolean;
  lines?: ManualEntryLineRequest[];
}

export interface ListManualEntriesQuery {
  entityId?: string;
  createdByUserId?: string;
  status?: GlManualEntryStatus;
  year?: number;
  dateFrom?: string;
  dateTo?: string;
  entryNumber?: string;
  documentType?: ManualEntryDocumentType;
  description?: string;
  debitMin?: number;
  debitMax?: number;
  creditMin?: number;
  creditMax?: number;
  page?: number;
  pageSize?: number;
  orderBy?: ManualEntryListOrderBy;
  orderDir?: ManualEntryListOrderDir;
}

export interface ListPendingAutoReverseQuery {
  entityId?: string;
  page?: number;
  pageSize?: number;
}

export interface ManualEntryAccountPickerQuery {
  entityId: string;
  journalDate?: string;
}

export interface ManualEntryLineResponse {
  lineId: string;
  lineNumber: number;
  glAccountId: string;
  debit: string;
  credit: string;
  description: string | null;
  costCenterId: string | null;
  departmentId: string | null;
  managerialAccountId: string | null;
  account?: {
    glAccountId: string;
    code: string;
    name: string;
  };
}

export interface ManualEntryAttachmentResponse {
  attachmentId: string;
  originalName: string;
  mimeType: string;
  fileSizeBytes: string;
  uploadedAt: string;
}

export interface ManualEntryWarning {
  code: string;
  message: string;
  userMessage: string;
}

export interface ManualEntryResponse {
  entryId: string;
  entityId: string;
  journalDate: string;
  documentType: string;
  description: string | null;
  status: GlManualEntryStatus;
  entryNumber: string;
  entryYear: number;
  autoReverse: boolean;
  autoReversePending: boolean;
  autoReverseTargetDate: string | null;
  source: GlManualEntrySource;
  sourceTransactionId: string | null;
  reversalSourceTransactionId: string | null;
  createdByUserId: string | null;
  createdBy?: {
    userId: string;
    name: string;
    email: string;
  } | null;
  postedByUserId: string | null;
  cancelledByUserId: string | null;
  reversedByUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
  postedAt: string | null;
  cancelledAt: string | null;
  reversedAt: string | null;
  lines: ManualEntryLineResponse[];
  attachments: ManualEntryAttachmentResponse[];
  warning?: ManualEntryWarning | null;
}

export interface ManualEntryListResponse {
  items: ManualEntryResponse[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PendingAutoReverseEntryResponse extends ManualEntryResponse {
  /**
   * Whether an OPEN period currently covers `autoReverseTargetDate`. Entries whose target period
   * was re-closed stay in the listing with `false` so the promise is never invisible.
   */
  targetPeriodOpen: boolean;
  /**
   * Whether the drain can post at all: an OPEN period exists at or after the target date, so the
   * posting door's forward clamp has somewhere to land. This — not `targetPeriodOpen` — is what
   * gates the drain action, because a re-closed target period is still drainable into the next
   * open one.
   */
  drainable: boolean;
  /**
   * The date the reversal would actually take (YYYY-MM-DD): the target date when its period is
   * open, otherwise the start of the first OPEN period after it. Null when nothing is open.
   */
  effectiveReversalDate: string | null;
}

export interface PendingAutoReverseListResponse {
  items: PendingAutoReverseEntryResponse[];
  total: number;
  page: number;
  pageSize: number;
}

export const MANUAL_ENTRY_POSTING_ERROR_CODES = [
  'JOURNAL_UNBALANCED',
  'JOURNAL_PERIOD_CLOSED',
  'JOURNAL_NO_OPEN_PERIOD',
  'JOURNAL_COA_PIN_MISMATCH',
  'JOURNAL_MIN_LINES',
  'JOURNAL_ACCOUNT_NOT_POSTABLE',
  'JOURNAL_ACCOUNT_NOT_ACTIVE',
  'JOURNAL_LINE_BOTH_SIDES',
  'JOURNAL_DATE_OUTSIDE_PERIOD',
] as const;

export type ManualEntryPostingErrorCode = (typeof MANUAL_ENTRY_POSTING_ERROR_CODES)[number];
